/**
 * useMission Hook — Fetches a single mission from LangGraph Store by ID
 *
 * Sprint 15: Replaced hardcoded mock with real Store query.
 */
import { useQuery } from '@tanstack/react-query';
import { NS } from '@ownyou/shared-types';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import type { Mission } from './useMissions';
import { getMissionCardLifecycleState, type CardFirstMissionCard } from '../lib/missions/lifecycle';

export function useMission(id: string) {
  const { store, isReady } = useStore();
  const { wallet } = useAuth();
  const userId = wallet?.address ?? 'anonymous';

  const query = useQuery({
    queryKey: ['mission', id, userId, isReady],
    queryFn: async (): Promise<Mission | null> => {
      if (!store || !isReady) return null;

      const namespace = NS.missionCards(userId);
      const result = await store.get<Mission>(namespace, id);
      if (!result) return null;

      return {
        ...result,
        state: getMissionCardLifecycleState(result as unknown as CardFirstMissionCard),
        createdAt: new Date(result.createdAt),
      };
    },
    enabled: !!id && isReady,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    mission: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
